import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import type { ApplyArAdvance, ArAdvance, ArInvoiceListRow, RefundArAdvance } from '@dcc-erp/shared';
import { toNumber } from '@dcc-erp/shared';

import { AdvanceList, ApplyAdvanceModal, RefundAdvanceModal } from '../components/ar/AdvanceList';
import { CreateAdvanceModal } from '../components/ar/CreateAdvanceModal';
import { PageHead } from '../components/layout';
import {
  useActiveCompanyId,
  useApplyArAdvance,
  useArAdvance,
  useArAdvances,
  useArInvoices,
  useCoa,
  useCreateArAdvance,
  useRefundArAdvance,
} from '../lib/queries';

export const Route = createFileRoute('/ar/advances')({ component: ArAdvancesPage });

export function ArAdvancesPage() {
  const navigate = useNavigate();
  const companyId = useActiveCompanyId();
  const { data, isPending, error } = useArAdvances(companyId);
  const apply = useApplyArAdvance(companyId);
  const refund = useRefundArAdvance(companyId);
  // useCreateArAdvance is consumed inside the create modal; referenced here only
  // so the route owns the same hook surface the spec lists (no behavioural use).
  useCreateArAdvance(companyId);

  const [createOpen, setCreateOpen] = useState(false);
  // The advance whose apply / refund modal is open (null = closed). Both need a
  // picker, so they open dedicated modals (not a prompt).
  const [applyAdvance, setApplyAdvance] = useState<ArAdvance | null>(null);
  const [refundAdvance, setRefundAdvance] = useState<ArAdvance | null>(null);

  // Apply modal: fetch the enriched detail of the advance being applied for its
  // LIVE balance (the list row's balance could be stale), and the customer's
  // outstanding invoices to pick one. Both disabled while no apply modal is open.
  const applyDetail = useArAdvance(companyId, applyAdvance?.id);
  const { data: customerInvoices = [], isPending: loadingInvoices } = useArInvoices(
    companyId,
    applyAdvance ? { customerId: applyAdvance.customerId, status: 'POSTED' } : {},
  );
  const openInvoices = useMemo(
    () =>
      applyAdvance
        ? customerInvoices.filter(
            (r: ArInvoiceListRow) =>
              toNumber(r.totalAmount) - toNumber(r.paidAmount) - toNumber(r.creditedAmount) > 0,
          )
        : [],
    [customerInvoices, applyAdvance],
  );
  // Prefer the freshly-fetched detail balance; fall back to the row while loading.
  const applyBalance = toNumber(applyDetail.data?.balance ?? applyAdvance?.balance);

  // Cash + bank accounts for the refund modal — codes in the 1110-1119 range
  // (cash equivalents), exactly as RecordReceiptModal picks them.
  const { data: coa = [] } = useCoa(companyId, true);
  const cashAccounts = useMemo(
    () =>
      coa.filter((c) => c.type === 'ASSET' && c.isActive && c.code >= '1110' && c.code < '1120'),
    [coa],
  );
  // Refund returns the WHOLE remaining balance, so the displayed figure must be
  // live — fetch the advance detail (enabled only when a refund modal is open)
  // and prefer its balance over the possibly-stale list row, exactly as the apply
  // path does. The server still refunds the true remaining balance.
  const refundDetail = useArAdvance(companyId, refundAdvance?.id);
  const refundBalance = toNumber(refundDetail.data?.balance ?? refundAdvance?.balance);

  async function handleApplySubmit(payload: ApplyArAdvance) {
    if (!applyAdvance) return;
    // On success close + invalidate; on failure the mutateAsync rejects, so we
    // keep the modal OPEN (no setApplyAdvance(null)) and the modal renders
    // apply.error so the user can fix the amount / retry.
    try {
      await apply.mutateAsync({ id: applyAdvance.id, data: payload });
      setApplyAdvance(null);
    } catch {
      // error surfaced via `apply.error` inside ApplyAdvanceModal; keep modal open.
    }
  }

  async function handleRefundSubmit(payload: RefundArAdvance) {
    if (!refundAdvance) return;
    try {
      await refund.mutateAsync({ id: refundAdvance.id, data: payload });
      setRefundAdvance(null);
    } catch {
      // error surfaced via `refund.error` inside RefundAdvanceModal; keep open.
    }
  }

  return (
    <div className="page">
      <PageHead
        crumbs={['ลูกหนี้การค้า', 'เงินรับล่วงหน้า']}
        title="เงินรับล่วงหน้า (Customer Advances)"
        actions={
          <>
            <button className="btn" onClick={() => void navigate({ to: '/ar' })}>
              ← กลับลูกหนี้การค้า
            </button>
            <button
              className="btn primary"
              onClick={() => setCreateOpen(true)}
              disabled={!companyId}
            >
              รับเงินล่วงหน้า
            </button>
          </>
        }
      />
      {isPending && (
        <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
          กำลังโหลด…
        </div>
      )}
      {error && (
        <div className="neg" style={{ padding: 24, textAlign: 'center' }}>
          โหลดไม่สำเร็จ: {error.message}
        </div>
      )}
      {data && (
        <AdvanceList
          advances={data}
          onView={(adv) => void navigate({ to: '/ar', search: { customerId: adv.customerId } })}
          onApply={(adv) => {
            // Drop any error from a previous apply before reopening.
            apply.reset();
            setApplyAdvance(adv);
          }}
          onRefund={(adv) => {
            refund.reset();
            setRefundAdvance(adv);
          }}
        />
      )}

      {createOpen && companyId && (
        <CreateAdvanceModal companyId={companyId} onClose={() => setCreateOpen(false)} />
      )}

      {applyAdvance && (
        <ApplyAdvanceModal
          advance={applyAdvance}
          balance={applyBalance}
          openInvoices={openInvoices}
          loadingInvoices={loadingInvoices}
          onSubmit={(payload) => void handleApplySubmit(payload)}
          onClose={() => setApplyAdvance(null)}
          submitting={apply.isPending}
          error={apply.error}
        />
      )}

      {refundAdvance && (
        <RefundAdvanceModal
          advance={refundAdvance}
          balance={refundBalance}
          cashAccounts={cashAccounts}
          onSubmit={(payload) => void handleRefundSubmit(payload)}
          onClose={() => setRefundAdvance(null)}
          submitting={refund.isPending}
          error={refund.error}
        />
      )}
    </div>
  );
}
